import { useState, useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import 'src/styles/index.css';
import 'src/App.css';

import Navigation from 'src/Components/NavigationTemplate/Navigation';
import TopBar from 'src/Components/TopBarTemplate/TopBar';
import Login from 'src/Login/Login';
import Main from 'src/Main/Main';

import './i18n'; // Path to your i18n config file

import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from 'src/lib/queryClient';

import { AuthProvider, useAuth } from 'src/Contexts/AuthContext';
import { SelectedInstallationProvider } from 'src/Contexts/SelectedInstallationContext';
import { ThemeContext } from 'src/Contexts/ThemeContext';
import { ToastProvider, useToast } from 'src/Contexts/ToastContext';
import ErrorBoundary from 'src/Components/ErrorBoundary/ErrorBoundary';
import { BrandProvider } from 'src/branding';

function AppWrapper() {
  return (
    <ErrorBoundary>
      <BrandProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ToastProvider>
              <App />
            </ToastProvider>
          </AuthProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </BrandProvider>
    </ErrorBoundary>
  );
}

function App() {
  const [DarkTheme, setDarkTheme] = useState(false);
  const { currentUser, sessionExpired, clearSessionExpired } = useAuth();
  const toast = useToast();
  const { t } = useTranslation();

  // When a token refresh fails, AuthContext flips sessionExpired; show the
  // message once (here, under ToastProvider) and reset the flag.
  useEffect(() => {
    if (sessionExpired) {
      toast.error(t('auth.sessionExpired'));
      clearSessionExpired();
    }
  }, [sessionExpired, toast, t, clearSessionExpired]);

  return (
    <ThemeContext.Provider value={{ DarkTheme, setDarkTheme }}>
      <BrowserRouter>
        <div className="App">
          <TopBar />
          <div className="main-content">
            {currentUser ? ( // Use currentUser to determine logged in status
              <>
                <div className="logged-in-content">
                  <Navigation />
                  <SelectedInstallationProvider>
                    <Main />
                  </SelectedInstallationProvider>
                </div>
              </>
            ) : (
              <>
                <div className="logged-out-content">
                  <Login />
                </div>
              </>
            )}
          </div>
        </div>
      </BrowserRouter>
    </ThemeContext.Provider>
  );
}

export default AppWrapper;
